import React, { useState, useEffect } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Alert,
  Text,
} from "@mantine/core";
import { IconAlertCircle, IconTrash } from "@tabler/icons-react";

const EditOperatingLevelTypeModal = ({
  opened,
  onClose,
  onSave,
  onDelete,
  operatingLevelType,
}) => {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const isTop = !!operatingLevelType?.top_operation_type;
  const isBase = !!operatingLevelType?.base_operation_type;
  const canDelete = !isTop && !isBase;

  useEffect(() => {
    if (opened && operatingLevelType) {
      setName(operatingLevelType.name || "");
      setError("");
      setShowDeleteConfirmation(false);
    }
  }, [opened, operatingLevelType]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await onSave({ ...operatingLevelType, name: name.trim() });
      onClose();
    } catch (err) {
      setError(err.message || "Failed to save operations type");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError("");
    setShowDeleteConfirmation(false);
    onClose();
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
    setError("");
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    setError("");
    try {
      await onDelete(operatingLevelType.id);
      handleClose();
    } catch (err) {
      setError(err.message || "Failed to delete operations type");
      setShowDeleteConfirmation(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Edit Operations Type"
      size="md"
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <TextInput
          label="Name"
          placeholder="Enter operations type name"
          value={name}
          onChange={e => setName(e.target.value)}
          required
        />

        {showDeleteConfirmation && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            <Stack gap="sm">
              <Text size="sm">
                Are you sure you want to delete "{operatingLevelType?.name}"?
                This action cannot be undone.
              </Text>
              <Group gap="sm">
                <Button
                  size="xs"
                  color="red"
                  onClick={handleDeleteConfirm}
                  loading={loading}
                >
                  Delete
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={handleDeleteCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        <Group justify="space-between" gap="sm">
          {canDelete && onDelete && !showDeleteConfirmation && (
            <Button
              variant="outline"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={handleDeleteClick}
              disabled={loading}
            >
              Delete
            </Button>
          )}

          <Group justify="flex-end" gap="sm" style={{ marginLeft: "auto" }}>
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              loading={loading}
              disabled={!name.trim() || showDeleteConfirmation}
            >
              Save Changes
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

export default EditOperatingLevelTypeModal;
